import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Select, TextField } from "components/Form/Field";
import usePromise from "helpers/usePromise";
import secteurDataProvider from "dataProvider/Secteur";
import familleDataProvider from "dataProvider/Famille";
import { Options } from "ui/Form/Element/Select";
import { IntlContext, ListContext } from "context";
import { useForm, useField } from "react-final-form";
import AutocompleteSelect, { OptionsInterface } from "ui/Field/AutocompleteSelect";
import { SelectedItemInterface } from "context/List/Context";
import { resetQueryString } from "helpers/url";

interface Props {
    hidden?: boolean;
}

const GroupFilter: React.FC<Props> = ({ hidden }) => {
    const intl = useContext(IntlContext);
    const { change, submit } = useForm();
    const form = useForm();
    const { selectedItems, setSelectedItems, removeSelectedItem, addSelectedItem, setActiveFetch } =
        useContext(ListContext);
    const [moreSelectedChoice, setMoreSelectedChoice] = useState<boolean>(false);
    const [activeSecteurs, setActiveSecteurs] = useState<boolean>();
    const fnameValue = useField("filter_name").input.value;
    const secteurValue = useField("filter_secteur").input.value;
    const familleValue = useField("filter_famille").input.value;
    const fValue = useField("f_famille").input.value;
    const refOptions = useRef<HTMLUListElement>(null);
    const [isTextClamped, setIsTextClamped] = useState<boolean>(false);

    useEffect(() => {
        const tagHeight = 40;
        if (refOptions?.current) {
            setIsTextClamped(
                refOptions.current.scrollHeight > refOptions.current.clientHeight ||
                    refOptions.current.clientHeight > tagHeight
            );
            if (refOptions.current.clientHeight <= tagHeight) {
                setMoreSelectedChoice(false);
            }
        }
    }, [refOptions, selectedItems]);

    const secteurData = usePromise(secteurDataProvider.all);
    const secteurOptions: Array<Options> = (secteurData.data || []).reduce((memo: Array<Options>, secteur: any) => {
        memo.push({
            label: secteur.name,
            value: secteur.secteur_id,
        });
        return memo;
    }, []);

    const submitSearch = useCallback(() => {
        setActiveFetch(true);
        resetQueryString();
        submit();
    }, [submit, setActiveFetch]);

    const handleMoreChoice = useCallback(
        (e: any) => {
            setMoreSelectedChoice(!moreSelectedChoice);
        },
        [setMoreSelectedChoice, moreSelectedChoice]
    );

    useEffect(() => {
        setActiveSecteurs(
            !(
                selectedItems.findIndex((i) => i.key === "famille") > -1 &&
                selectedItems.findIndex((i) => i.key === "secteur") === -1
            )
        );
    }, [setActiveSecteurs, selectedItems]);

    const hasItemsSelected = useCallback(
        (minItem: number = 1) => {
            return selectedItems.length >= minItem;
        },
        [selectedItems]
    );

    const onFamilleSelect = (value: any, record: any, selectedValues: OptionsInterface[]) => {
        const selectedFamilles: SelectedItemInterface[] = selectedValues.reduce(
            (memo: SelectedItemInterface[], opt: OptionsInterface) => {
                if (selectedItems.findIndex((i) => i.key === "famille" && i.value === opt.value) === -1) {
                    memo.push({
                        key: "famille",
                        label: opt.label,
                        value: opt.value,
                    });
                }
                return memo;
            },
            []
        );
        const newSelectedItems = [...selectedItems, ...selectedFamilles];
        setSelectedItems(newSelectedItems);

        const values: string = newSelectedItems
            .reduce((memo: string[], i: SelectedItemInterface) => {
                if (i.key === "famille") {
                    memo.push(i.value);
                }
                return memo;
            }, [])
            .join("|");

        change("filter_famille", values.length ? values : undefined);

        submitSearch();
    };

    const removeChoiceSelected = useCallback(
        (key: string, value: any) => {
            removeSelectedItem(key, value);
        },
        [removeSelectedItem]
    );

    const choiceSelectedList = () => {
        const list = selectedItems.map((item: { key: string; label: string; value: any }, index: number) => {
            return (
                <li
                    key={index}
                    className="choice-element"
                    onClick={() => {
                        removeChoiceSelected(item.key, item.value);

                        let itemValues: string;
                        let otherValues: string;

                        if (item.key === "secteur") {
                            itemValues = secteurValue;
                            otherValues = familleValue;
                        } else {
                            //famille
                            itemValues = familleValue;
                            otherValues = secteurValue;
                        }
                        const itemKey = item.key;
                        const newItemValues = (itemValues || "")
                            .split("|")
                            .filter((value: string) => value !== item.value)
                            .join("|");
                        change(`filter_${itemKey}`, newItemValues || undefined);

                        if (!newItemValues?.length && !otherValues?.length && !fnameValue?.length) {
                            resetQueryString();
                            form.reset({});
                            setActiveFetch(false);
                            return;
                        }
                        submitSearch();
                    }}
                >
                    <span className="choice-element__text">
                        <i className="icon icon-cross"></i>
                        {item.label}
                    </span>
                </li>
            );
        });
        return <React.Fragment>{list}</React.Fragment>;
    };

    const onSecteurChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (!e?.target?.value) {
            return;
        }
        const value = e.target.value;
        const index = e.target.selectedIndex;
        const label = e.target[index].textContent as string;
        addSelectedItem({
            key: "secteur",
            label,
            value,
        });

        const values: string = selectedItems
            .reduce((memo: string[], i: SelectedItemInterface) => {
                if (i.key === "secteur") {
                    memo.push(i.value);
                }
                return memo;
            }, [])
            .join("|");

        change("filter_secteur", values.length ? values : undefined);
        change("f_secteur", undefined);

        submitSearch();
    };

    useEffect(() => {
        if (selectedItems.length === 0) {
            setMoreSelectedChoice(false);
        }
    }, [selectedItems, setMoreSelectedChoice]);

    return (
        <React.Fragment>
            <div className={`searchbar-select-group ${hidden ? "hidden" : ""}`}>
                {/* SECTEUR SELECT */}
                <Select
                    inputProps={{
                        name: "filter_s",
                        placeholder: intl.formatMessage("produit.label.secteur"),
                        options: secteurOptions.filter(
                            (option: Options) =>
                                (selectedItems || []).findIndex(
                                    (item: SelectedItemInterface) =>
                                        item.key === "secteur" && "" + item.value === "" + option.value
                                ) === -1
                        ),
                        disabled: !activeSecteurs,
                        onChange: onSecteurChange,
                    }}
                />
                <TextField
                    inputProps={{
                        name: "filter_secteur",
                        type: "hidden",
                    }}
                />

                {/* FAMILLE AUTOCOMPLETE SELECT */}
                <AutocompleteSelect
                    inputProps={{
                        name: "f_famille",
                        value: fValue,
                        placeholder: intl.formatMessage("produit.label.famille"),
                        keySearch: "filter_name",
                        dataValue: "famille_id",
                        dataLabel: "name",
                        autoComplete: "off",
                        dataFetcher: familleDataProvider.autocomplete,
                        minSearch: 0,
                        defaultParams: {
                            filter_secteur_id:
                                selectedItems
                                    .reduce((memo: any[], item) => {
                                        if (item.key === "secteur") {
                                            memo.push(item.value);
                                        }
                                        return memo;
                                    }, [])
                                    .join("|") || null,
                        },
                        onSelectOptionCallback: onFamilleSelect,
                        values: selectedItems.reduce((memo: any[], item) => {
                            if (item.key === "famille") {
                                memo.push({
                                    label: item.label,
                                    value: item.value,
                                });
                            }
                            return memo;
                        }, []),
                    }}
                />
                <TextField
                    inputProps={{
                        name: "filter_famille",
                        type: "hidden",
                    }}
                />
            </div>

            {/* TAGS SECTEURS/FAMILLES SELECTIONNES */}
            <ul
                ref={refOptions}
                className={`choice-selected-list ${moreSelectedChoice ? "choice-selected-list--more" : ""}`}
            >
                {choiceSelectedList()}
            </ul>

            {/* DISPLAY BUTTONS */}
            {isTextClamped && (
                <Link
                    to="#"
                    onClick={handleMoreChoice}
                    className={`cta cta--link see-${
                        hasItemsSelected() ? "more" : "less"
                    }-choices float-right no-padding`}
                    title={
                        moreSelectedChoice
                            ? intl.formatMessage("generic.close_all")
                            : intl.formatMessage("generic.see_all")
                    }
                >
                    {moreSelectedChoice
                        ? intl.formatMessage("generic.close_all")
                        : intl.formatMessage("generic.see_all")}
                </Link>
            )}
        </React.Fragment>
    );
};

export default GroupFilter;
